import React, { useState, useEffect, useMemo, useRef } from "react";
import { bPEdMPEdProgrammeApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./BPEdMPEdProgramme.scss";

const PROGRAMME_TYPES  = ["B.P.Ed", "M.P.Ed"];
const SPECIALIZATIONS  = [
  "Athletics & Track",
  "Aquatics & Swimming",
  "Ball Games & Team Sports",
  "Gymnastics & Yoga",
  "Outdoor & Adventure Sports",
  "Sports Management",
  "Exercise Science & Fitness",
  "Racket Sports",
];
const DURATIONS        = ["1 Year", "2 Years", "3 Years"];
const MODES            = ["Full Time", "Part Time", "Distance"];

const SPEC_COLORS = {
  "Athletics & Track":           "#ef4444",
  "Aquatics & Swimming":         "#0ea5e9",
  "Ball Games & Team Sports":    "#f59e0b",
  "Gymnastics & Yoga":           "#8b5cf6",
  "Outdoor & Adventure Sports":  "#10b981",
  "Sports Management":           "#6366f1",
  "Exercise Science & Fitness":  "#ec4899",
  "Racket Sports":               "#f97316",
};
const TYPE_META = {
  "B.P.Ed": { color: "#6366f1", bg: "#eef2ff", label: "Bachelor of Physical Education" },
  "M.P.Ed": { color: "#8b5cf6", bg: "#f5f3ff", label: "Master of Physical Education"   },
};
const DEFAULT_COLOR = "#64748b";

const mkProgrammeSubject = () => ({
  id: Date.now() + Math.random(),
  Subject_Name: "",
  Total_Seats: "",
  Group: ""
});

const initialProgrammes = [
  {
    id: 1,
    programmeType: "B.P.Ed",
    name: "B.P.Ed — Athletics & Track",
    code: "BPED-AT",
    specialization: "Athletics & Track",
    duration: "2 Years",
    semesters: 4,
    totalSeats: 60,
    annualFees: "₹24,000",
    mode: "Full Time",
    eligibility: "10+2 from a recognized board with minimum 50% marks. Candidates must have participated in sports/games at district/state level. A physical fitness test is mandatory.",
    minQualification: "10+2 (Any Stream)",
    description: "A professional undergraduate programme training students in athletic coaching, track & field techniques, sports biomechanics, and physical education pedagogy for school and sports academy settings.",
    affiliation: "Karnataka State University",
    accreditation: "NCTE Approved",
    practicalHours: "800 Hours Field & Practical Training",
    fitnessTest: "Mandatory Physical Fitness Entrance Test",
    highlights: "Track & Field Lab, Biomechanics, Coaching Practice, School Teaching Internship",
    status: "published",
  },
  {
    id: 2,
    programmeType: "B.P.Ed",
    name: "B.P.Ed — Ball Games & Team Sports",
    code: "BPED-BGT",
    specialization: "Ball Games & Team Sports",
    duration: "2 Years",
    semesters: 4,
    totalSeats: 60,
    annualFees: "₹24,000",
    mode: "Full Time",
    eligibility: "10+2 from a recognized board with minimum 50% marks. Candidates must have represented their school/district in any team sport. Physical fitness test required.",
    minQualification: "10+2 (Any Stream)",
    description: "A programme developing skilled physical education teachers and coaches specializing in team ball sports including football, basketball, volleyball, hockey, and kho-kho for schools and sports institutions.",
    affiliation: "Karnataka State University",
    accreditation: "NCTE Approved",
    practicalHours: "800 Hours Field & Practical Training",
    fitnessTest: "Mandatory Physical Fitness Entrance Test",
    highlights: "Multi-sport Courts, Team Coaching, Game Strategy, School Internship",
    status: "published",
  },
  {
    id: 3,
    programmeType: "B.P.Ed",
    name: "B.P.Ed — Gymnastics & Yoga",
    code: "BPED-GY",
    specialization: "Gymnastics & Yoga",
    duration: "2 Years",
    semesters: 4,
    totalSeats: 40,
    annualFees: "₹26,000",
    mode: "Full Time",
    eligibility: "10+2 from a recognized board with minimum 50% marks. Prior experience in gymnastics or yoga preferred. Physical fitness test required at admission.",
    minQualification: "10+2 (Any Stream)",
    description: "A comprehensive programme combining classical gymnastics training with traditional yoga pedagogy, preparing certified instructors and PE teachers for wellness centres, schools, and sports academies.",
    affiliation: "Karnataka State University",
    accreditation: "NCTE Approved",
    practicalHours: "800 Hours Field & Practical Training",
    fitnessTest: "Mandatory Physical Fitness Entrance Test",
    highlights: "Gymnastics Arena, Yoga Studio, Flexibility Training, School Practice",
    status: "published",
  },
  {
    id: 4,
    programmeType: "B.P.Ed",
    name: "B.P.Ed — Aquatics & Swimming",
    code: "BPED-AQ",
    specialization: "Aquatics & Swimming",
    duration: "2 Years",
    semesters: 4,
    totalSeats: 30,
    annualFees: "₹28,000",
    mode: "Full Time",
    eligibility: "10+2 from a recognized board with minimum 50% marks. Candidates must be competent swimmers. Physical fitness test and swimming proficiency test required.",
    minQualification: "10+2 (Any Stream) + Swimming Proficiency",
    description: "A specialized programme training aquatics coaches and swimming instructors with expertise in competitive swimming, water polo, lifesaving, and aqua fitness for schools and sports complexes.",
    affiliation: "Karnataka State University",
    accreditation: "NCTE Approved",
    practicalHours: "900 Hours Pool & Field Training",
    fitnessTest: "Swimming Proficiency + Physical Fitness Test",
    highlights: "Olympic Pool, Lifesaving Certification, Stroke Technique, Aqua Coaching",
    status: "draft",
  },
  {
    id: 5,
    programmeType: "M.P.Ed",
    name: "M.P.Ed — Sports Management",
    code: "MPED-SM",
    specialization: "Sports Management",
    duration: "2 Years",
    semesters: 4,
    totalSeats: 30,
    annualFees: "₹38,000",
    mode: "Full Time",
    eligibility: "B.P.Ed or B.Sc. (Physical Education) with minimum 50% marks from a recognized university. Candidates with sports administration background preferred.",
    minQualification: "B.P.Ed / B.Sc. Physical Education",
    description: "A postgraduate programme developing sports administrators, event managers, and physical education directors with expertise in sports governance, facility management, sponsorship, and athlete welfare.",
    affiliation: "Karnataka State University",
    accreditation: "NCTE Approved",
    practicalHours: "600 Hours Practical + Sports Management Internship",
    fitnessTest: "Physical Fitness Test at Admission",
    highlights: "Sports Event Management, Facility Planning, Research Dissertation, Industry Internship",
    status: "published",
  },
  {
    id: 6,
    programmeType: "M.P.Ed",
    name: "M.P.Ed — Exercise Science & Fitness",
    code: "MPED-ESF",
    specialization: "Exercise Science & Fitness",
    duration: "2 Years",
    semesters: 4,
    totalSeats: 30,
    annualFees: "₹40,000",
    mode: "Full Time",
    eligibility: "B.P.Ed or B.Sc. (Physical Education / Sports Science) with minimum 50% marks from a recognized university.",
    minQualification: "B.P.Ed / B.Sc. Sports Science",
    description: "An advanced programme in exercise physiology, strength & conditioning, sports nutrition, and rehabilitation, preparing fitness specialists and sports science researchers for elite sports and clinical settings.",
    affiliation: "Karnataka State University",
    accreditation: "NCTE Approved",
    practicalHours: "600 Hours Practical + Lab Research",
    fitnessTest: "Physical Fitness Test at Admission",
    highlights: "Physiology Lab, Strength & Conditioning, Nutrition Science, Research Dissertation",
    status: "published",
  },
];

const defaultForm = {
  _id: null,
  Programme_Type: "B.P.Ed",
  Programme_Name: "",
  Programme_Code: "",
  Sports_Specialization: "",
  Duration: "2 Years",
  Total_Semesters: "",
  Total_Seats: "",
  Annual_Fees: "",
  Mode_of_Study: "Full Time",
  Full_Eligibility_Criteria: "",
  Minimum_Qualification_Required: "",
  Programme_Description: "",
  Affiliation: "",
  Accreditation_Approval: "NCTE Approved",
  Practical_Field_Training_Hours: "",
  Fitness_Entrance_Test: "",
  Programme_Highlights: "",
  Programme_Subjects: [],
  Status: "Published",
};

const getSpecColor  = (spec)  => SPEC_COLORS[spec]  || DEFAULT_COLOR;
const getTypeColor  = (type)  => TYPE_META[type]?.color || DEFAULT_COLOR;
const TABS = ["details", "academic", "eligibility", "settings"];

const BPEdMPEdProgramme = () => {
  const [programmes, setProgrammes]         = useState([]);
  const [loading, setLoading]               = useState(false);
  const [modalType, setModalType]           = useState("");
  const [formData, setFormData]             = useState(defaultForm);
  const [selectedItem, setSelectedItem]     = useState(null);
  const [confirmId, setConfirmId]           = useState(null);
  const [confirmAction, setConfirmAction]   = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos]       = useState({ top: 0, left: 0 });
  const [filterType, setFilterType]         = useState("all");
  const [filterSpec, setFilterSpec]         = useState("all");
  const [filterStatus, setFilterStatus]     = useState("all");
  const [searchText, setSearchText]         = useState("");
  const [activeTab, setActiveTab]           = useState("details");
  const dropdownRef = useRef(null);

  useEffect(() => { fetchProgrammes(); }, []);

  const fetchProgrammes = async () => {
    try {
      setLoading(true);
      const response = await apiClient(bPEdMPEdProgrammeApi);
      const result = await response.json();
      if (response.ok) { setProgrammes(result.data || []); }
    } catch (error) {
      console.error("Error fetching B.P.Ed/M.P.Ed programmes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onOut   = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpenId(null); };
    const onClose = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", onOut);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onOut);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, []);

  const filtered = useMemo(() => {
    return programmes.filter((p) => {
      const matchType   = filterType   === "all" || p.Programme_Type  === filterType;
      const matchSpec   = filterSpec   === "all" || p.Sports_Specialization === filterSpec;
      const matchStatus = filterStatus === "all" || p.Status         === filterStatus;
      const matchSearch =
        (p.Programme_Name || "").toLowerCase().includes(searchText.toLowerCase()) ||
        (p.Programme_Code || "").toLowerCase().includes(searchText.toLowerCase()) ||
        (p.Sports_Specialization || "").toLowerCase().includes(searchText.toLowerCase());
      return matchType && matchSpec && matchStatus && matchSearch;
    });
  }, [programmes, filterType, filterSpec, filterStatus, searchText]);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) { setDropdownOpenId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const mW = 205, mH = 170, gap = 8;
    let left = rect.right - mW;
    let top  = rect.bottom + gap;
    if (left < 12) left = 12;
    if (left + mW > window.innerWidth  - 12) left = window.innerWidth  - mW - 12;
    if (top  + mH > window.innerHeight - 12) top  = rect.top - mH - gap;
    if (top  < 12) top  = 12;
    setDropdownPos({ top, left });
    setDropdownOpenId(id);
  };

  const openAddModal  = ()  => { setFormData(defaultForm); setActiveTab("details"); setModalType("add");  setDropdownOpenId(null); };
  const openViewModal = (p) => { if(!p) return; setSelectedItem(p); setModalType("view"); setDropdownOpenId(null); };
  const openEditModal = (p) => { if(!p) return; setFormData({ ...defaultForm, ...p }); setActiveTab("details"); setModalType("edit"); setDropdownOpenId(null); };
  const closeModal    = ()  => { setModalType(""); setSelectedItem(null); setFormData(defaultForm); };
  const openConfirm   = (id, action) => { setDropdownOpenId(null); setConfirmId(id); setConfirmAction(action); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? bPEdMPEdProgrammeApi : `${bPEdMPEdProgrammeApi}/${formData._id}`;
      const response = await apiClient(url, { method, body: JSON.stringify(formData) });
      if (response.ok) { fetchProgrammes(); closeModal(); }
    } catch (error) {
      console.error("Error saving programme:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      setLoading(true);
      const item = programmes.find(p => p._id === id);
      const newStatus = item.Status === "Published" ? "Draft" : "Published";
      await apiClient(`${bPEdMPEdProgrammeApi}/${id}`, { method: "PUT", body: JSON.stringify({ ...item, Status: newStatus }) });
      fetchProgrammes(); setConfirmId(null); setConfirmAction("");
    } catch (error) {
      console.error("Error toggling status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await apiClient(`${bPEdMPEdProgrammeApi}/${id}`, { method: "DELETE" });
      fetchProgrammes(); setConfirmId(null); setConfirmAction("");
    } catch (error) {
      console.error("Error deleting programme:", error);
    } finally {
      setLoading(false);
    }
  };

  const bpedCount      = programmes.filter((p) => p.Programme_Type === "B.P.Ed").length;
  const mpedCount      = programmes.filter((p) => p.Programme_Type === "M.P.Ed").length;
  const publishedCount = programmes.filter((p) => p.Status === "Published").length;
  const totalSeats     = programmes.reduce((s, p) => s + Number(p.Total_Seats || 0), 0);
  const currentDDItem  = programmes.find((p) => p._id === dropdownOpenId);

  return (
    <div className="bped-page">

      {/* ── Header ── */}
      <div className="bped-header">
        <div className="bped-header-left">
          <div className="bped-header-icon">🏃</div>
          <div>
            <div className="bped-breadcrumb">
              <span>Academics</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              <span>Academic Programmes</span>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              <span className="bped-breadcrumb-active">B.P.Ed / M.P.Ed</span>
            </div>
            <h1>B.P.Ed / M.P.Ed Programme</h1>
            <p>Manage Bachelor and Master of Physical Education programmes — add, edit, publish, and control visibility on the college website.</p>
          </div>
        </div>
        <button type="button" className="bped-add-btn" onClick={openAddModal}>
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add Programme
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="bped-stats">
        {[
          { cls: "bped",      icon: "🏃", label: "B.P.Ed Programmes", val: bpedCount      },
          { cls: "mped",      icon: "🎓", label: "M.P.Ed Programmes", val: mpedCount      },
          { cls: "published", icon: "🌐", label: "Published",          val: publishedCount },
          { cls: "seats",     icon: "🪑", label: "Total Seats",        val: totalSeats     },
        ].map((s) => (
          <div key={s.cls} className={`bped-stat-card ${s.cls}`}>
            <div className="stat-icon-box">{s.icon}</div>
            <div className="stat-content">
              <span>{s.label}</span>
              <strong>{s.val}</strong>
            </div>
          </div>
        ))}
      </div>

      {/* ── Type Toggle ── */}
      <div className="bped-type-toggle">
        {["all", "B.P.Ed", "M.P.Ed"].map((t) => (
          <button
            key={t}
            type="button"
            className={`type-toggle-btn ${filterType === t ? "active" : ""}`}
            onClick={() => setFilterType(t)}
          >
            {t === "all" ? (
              <><span>⚡</span> All Programmes ({programmes.length})</>
            ) : t === "B.P.Ed" ? (
              <><span>🏃</span> B.P.Ed ({bpedCount})</>
            ) : (
              <><span>🎓</span> M.P.Ed ({mpedCount})</>
            )}
          </button>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="bped-filter-card">
        <div className="bped-filter-grid">
          <div className="fg">
            <label>Search</label>
            <div className="search-wrap">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Name, code or specialization…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button type="button" className="clear-search" onClick={() => setSearchText("")}>×</button>
              )}
            </div>
          </div>
          <div className="fg">
            <label>Specialization</label>
            <select value={filterSpec} onChange={(e) => setFilterSpec(e.target.value)}>
              <option value="all">All Specializations</option>
              {SPECIALIZATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="fg">
            <label>Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All</option>
              <option value="Published">Published</option>
              <option value="Draft">Draft</option>
            </select>
          </div>
        </div>
        <div className="bped-filter-meta">
          Showing <strong>{filtered.length}</strong> of <strong>{programmes.length}</strong> programmes
          {(filterType !== "all" || filterSpec !== "all" || filterStatus !== "all" || searchText) && (
            <button
              type="button"
              className="clear-filters-btn"
              onClick={() => { setFilterType("all"); setFilterSpec("all"); setFilterStatus("all"); setSearchText(""); }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* ── Cards ── */}
      {filtered.length > 0 ? (
        <div className="bped-cards-grid">
          {filtered.map((prog) => {
            const specColor = getSpecColor(prog.specialization);
            const typeColor = getTypeColor(prog.programmeType);
            const highlights = prog.highlights
              ? prog.highlights.split(",").map((h) => h.trim()).filter(Boolean)
              : [];
            return (
              <div key={prog._id} className={`bped-card ${prog.Status}`}>
                <div className="bped-card-bar" style={{ background: `linear-gradient(90deg, ${specColor}, ${specColor}88)` }} />

                <div className="bped-card-head">
                  <div className="bped-type-pill" style={{ background: TYPE_META[prog.Programme_Type]?.bg, color: typeColor }}>
                    {prog.Programme_Type === "B.P.Ed" ? "🏃" : "🎓"} {prog.Programme_Type}
                  </div>
                  <div className="bped-card-actions">
                    <span className={`bped-status-dot ${prog.Status}`} />
                    <button type="button" className="dots-btn" onClick={(e) => handleDotsClick(e, prog._id)}>⋮</button>
                  </div>
                </div>

                <div className="bped-card-body">
                  <div className="bped-card-code-row">
                    <span className="bped-card-code">{prog.Programme_Code}</span>
                    {prog.Accreditation_Approval && (
                      <span className="bped-accred-tag">{prog.Accreditation_Approval}</span>
                    )}
                  </div>
                  {prog.Sports_Specialization && (
                    <div className="bped-spec-label" style={{ color: specColor }}>
                      ● {prog.Sports_Specialization}
                    </div>
                  )}
                  <h3 className="bped-card-title" onClick={() => openViewModal(prog)}>{prog.Programme_Name}</h3>
                  <p className="bped-card-desc">{prog.Programme_Description}</p>
                </div>

                <div className="bped-card-meta">
                  <div className="meta-chip">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    {prog.Duration}
                  </div>
                  <div className="meta-chip">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    {prog.Total_Seats} Seats
                  </div>
                  <div className="meta-chip">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                    {prog.Annual_Fees}/yr
                  </div>
                  <div className="meta-chip">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                    {prog.Total_Semesters} Semesters
                  </div>
                </div>

                {prog.Practical_Field_Training_Hours && (
                  <div className="bped-practical-strip" style={{ borderLeft: `3px solid ${specColor}` }}>
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke={specColor} strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    <span>{prog.Practical_Field_Training_Hours}</span>
                  </div>
                )}

                {prog.Fitness_Entrance_Test && (
                  <div className="bped-fitness-strip">
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="#ef4444" strokeWidth="2">
                      <path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/>
                    </svg>
                    <span>{prog.Fitness_Entrance_Test}</span>
                  </div>
                )}

                {highlights.length > 0 && (
                  <div className="bped-card-highlights">
                    {highlights.slice(0, 3).map((h, i) => (
                      <span key={i} className="hl-chip" style={{ background: specColor + "12", color: specColor, borderColor: specColor + "25" }}>{h}</span>
                    ))}
                    {highlights.length > 3 && <span className="hl-chip more">+{highlights.length - 3}</span>}
                  </div>
                )}

                <div className="bped-card-footer">
                  <span className={`bped-status-badge ${prog.Status}`}>
                    {prog.Status === "Published" ? "● Published" : "● Draft"}
                  </span>
                  <div className="bped-card-btns">
                    <button type="button" className="card-btn-view" onClick={() => openViewModal(prog)}>View</button>
                    <button type="button" className="card-btn-edit" onClick={() => openEditModal(prog)}>Edit</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bped-empty">
          <div className="empty-icon">🏃</div>
          <h3>No programmes found</h3>
          <p>
            {searchText || filterType !== "all" || filterSpec !== "all" || filterStatus !== "all"
              ? "Try adjusting your filters or search term."
              : "No B.P.Ed / M.P.Ed programmes added yet. Click \"Add Programme\" to get started."}
          </p>
          {!searchText && filterType === "all" && filterSpec === "all" && filterStatus === "all" && (
            <button type="button" className="bped-add-btn" onClick={openAddModal}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Programme
            </button>
          )}
        </div>
      )}

      {/* ── Three-dot Dropdown ── */}
      {dropdownOpenId !== null && (
        <div
          ref={dropdownRef}
          className="action-dropdown"
          style={{ position: "fixed", top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px`, zIndex: 99999 }}
        >
          <button type="button" onClick={() => openViewModal(programmes.find((p) => p._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
            View Details
          </button>
          <button type="button" onClick={() => openEditModal(programmes.find((p) => p._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
            Edit Programme
          </button>
          {currentDDItem && (
            <button
              type="button"
              className={currentDDItem.Status === "Published" ? "deactivate" : "activate"}
              onClick={() => openConfirm(dropdownOpenId, currentDDItem.Status === "Published" ? "unpublish" : "publish")}
            >
              {currentDDItem.Status === "Published" ? (
                <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/></svg>Unpublish</>
              ) : (
                <><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>Publish</>
              )}
            </button>
          )}
          <div className="dropdown-divider" />
          <button type="button" className="delete" onClick={() => openConfirm(dropdownOpenId, "delete")}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
            Delete
          </button>
        </div>
      )}

      {/* ── Confirm Modal ── */}
      {confirmId !== null && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`confirm-icon-box ${confirmAction}`}>
              {confirmAction === "delete" ? (
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              )}
            </div>
            <h3>
              {confirmAction === "delete" ? "Delete Programme?" :
               confirmAction === "unpublish" ? "Unpublish Programme?" : "Publish Programme?"}
            </h3>
            <p>
              {confirmAction === "delete"
                ? "This programme will be permanently deleted and cannot be recovered."
                : confirmAction === "unpublish"
                ? "This programme will be hidden from the public college website."
                : "This programme will be visible on the public college website."}
            </p>
            <div className="confirm-actions">
              <button type="button" className="btn btn-light" onClick={() => setConfirmId(null)}>Cancel</button>
              <button
                type="button"
                className={`btn ${confirmAction === "delete" ? "btn-danger" : "btn-warning"}`}
                onClick={() => confirmAction === "delete" ? handleDelete(confirmId) : handleTogglePublish(confirmId)}
              >
                {confirmAction === "delete" ? "Yes, Delete" :
                 confirmAction === "unpublish" ? "Yes, Unpublish" : "Yes, Publish"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {(modalType === "add" || modalType === "edit") && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="bped-modal" onClick={(e) => e.stopPropagation()}>

            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">🏃</div>
                <div>
                  <h2>{modalType === "add" ? "Add B.P.Ed / M.P.Ed Programme" : "Edit Programme"}</h2>
                  <span>{modalType === "add" ? "Create a new physical education programme" : `Editing: ${formData.name || "Programme"}`}</span>
                </div>
              </div>
              <button type="button" className="modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="modal-tabs">
              {[
                { key: "details",     label: "Basic Details", icon: "📋" },
                { key: "academic",    label: "Academic Info",  icon: "📚" },
                { key: "eligibility", label: "Eligibility",    icon: "✅" },
                { key: "settings",    label: "Settings",       icon: "⚙️" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`modal-tab ${activeTab === tab.key ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <span>{tab.icon}</span>{tab.label}
                </button>
              ))}
            </div>

            <form className="modal-form" onSubmit={handleSave}>

              {/* ── Tab 1: Basic Details ── */}
              {activeTab === "details" && (
                <div className="tab-content">

                  {/* Programme Type Selector */}
                  <div className="form-group full">
                    <label>Programme Type <span className="req">*</span></label>
                    <div className="type-selector">
                      {PROGRAMME_TYPES.map((t) => (
                        <label key={t} className={`type-option ${formData.Programme_Type === t ? "selected" : ""}`}
                          style={formData.Programme_Type === t ? { borderColor: TYPE_META[t].color, background: TYPE_META[t].bg, boxShadow: `0 0 0 3px ${TYPE_META[t].color}20` } : {}}>
                          <input type="radio" name="Programme_Type" value={t} checked={formData.Programme_Type === t} onChange={handleChange} />
                          <div className="type-option-content">
                            <div className="type-icon" style={formData.Programme_Type === t ? { background: TYPE_META[t].color + "20" } : {}}>
                              {t === "B.P.Ed" ? "🏃" : "🎓"}
                            </div>
                            <div>
                              <strong style={formData.Programme_Type === t ? { color: TYPE_META[t].color } : {}}>{t}</strong>
                              <span>{TYPE_META[t].label}</span>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group full">
                    <label>Programme Name <span className="req">*</span></label>
                    <input type="text" name="Programme_Name" value={formData.Programme_Name} onChange={handleChange}
                      placeholder={`e.g. ${formData.Programme_Type} — Athletics & Track`} required />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Programme Code <span className="req">*</span></label>
                      <input type="text" name="Programme_Code" value={formData.Programme_Code} onChange={handleChange}
                        placeholder="e.g. BPED-AT" required />
                    </div>
                    <div className="form-group">
                      <label>Sports Specialization</label>
                      <select name="Sports_Specialization" value={formData.Sports_Specialization} onChange={handleChange}>
                        <option value="">Select specialization</option>
                        {SPECIALIZATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group full">
                    <label>Programme Description <span className="req">*</span></label>
                    <textarea name="Programme_Description" value={formData.Programme_Description} onChange={handleChange}
                      placeholder="Describe the programme, sports focus, coaching methodology, and career outcomes..."
                      rows="4" required />
                  </div>
                  <div className="form-group full">
                    <label>Programme Highlights</label>
                    <input type="text" name="Programme_Highlights" value={formData.Programme_Highlights} onChange={handleChange}
                      placeholder="e.g. Track Lab, Coaching Practice, Sports Internship (comma-separated)" />
                    <span className="field-hint">Separate with commas — displayed as tags on the website.</span>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Practical / Field Training Hours</label>
                      <input type="text" name="Practical_Field_Training_Hours" value={formData.Practical_Field_Training_Hours} onChange={handleChange}
                        placeholder="e.g. 800 Hours Field & Practical Training" />
                      <span className="field-hint">Shown as a timed strip on the programme card.</span>
                    </div>
                    <div className="form-group">
                      <label>Fitness / Entrance Test</label>
                      <input type="text" name="Fitness_Entrance_Test" value={formData.Fitness_Entrance_Test} onChange={handleChange}
                        placeholder="e.g. Mandatory Physical Fitness Entrance Test" />
                      <span className="field-hint">Displayed below the practical hours strip.</span>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Tab 2: Academic Info ── */}
              {activeTab === "academic" && (
                <div className="tab-content">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Duration <span className="req">*</span></label>
                      <select name="Duration" value={formData.Duration} onChange={handleChange} required>
                        <option value="">Select duration</option>
                        {DURATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Total Semesters <span className="req">*</span></label>
                      <input type="number" name="Total_Semesters" value={formData.Total_Semesters} onChange={handleChange}
                        placeholder="e.g. 4" min="1" max="8" required />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Total Seats <span className="req">*</span></label>
                      <input type="number" name="Total_Seats" value={formData.Total_Seats} onChange={handleChange}
                        placeholder="e.g. 60" min="1" required />
                    </div>
                    <div className="form-group">
                      <label>Annual Fees</label>
                      <input type="text" name="Annual_Fees" value={formData.Annual_Fees} onChange={handleChange}
                        placeholder="e.g. ₹24,000" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Mode of Study</label>
                      <select name="Mode_of_Study" value={formData.Mode_of_Study} onChange={handleChange}>
                        {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Affiliation</label>
                      <input type="text" name="Affiliation" value={formData.Affiliation} onChange={handleChange}
                        placeholder="e.g. Karnataka State University" />
                    </div>
                  </div>
                  <div className="form-group full">
                    <label>Accreditation / Approval</label>
                    <input type="text" name="Accreditation_Approval" value={formData.Accreditation_Approval} onChange={handleChange}
                      placeholder="e.g. NCTE Approved, NAAC A+" />
                  </div>

                  <div className="form-section-label" style={{ marginTop: "24px", marginBottom: "12px", fontWeight: "600", color: "#334155" }}>Programme Subjects</div>
                  <div className="subject-list">
                    {formData.Programme_Subjects && formData.Programme_Subjects.map((sub, idx) => (
                      <div key={sub.id || idx} className="subject-item-card" style={{ background: "#f8fafc", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "16px" }}>
                        <div className="sic-header" style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", fontWeight: "600", fontSize: "14px", color: "#475569" }}>
                          <span>Subject #{idx + 1}</span>
                          <button type="button" style={{ color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontSize: "13px" }} onClick={() => setFormData({...formData, Programme_Subjects: formData.Programme_Subjects.filter((_, i) => i !== idx)})}>Remove</button>
                        </div>
                        <div className="form-row" style={{ display: "flex", gap: "16px" }}>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label style={{ display: "block", marginBottom: "8px", fontSize: "13px", fontWeight: "500", color: "#64748b" }}>Subject Name</label>
                            <input 
                              type="text"
                              value={sub.Subject_Name || ""} 
                              onChange={e => { const s = [...(formData.Programme_Subjects || [])]; s[idx].Subject_Name = e.target.value; setFormData({...formData, Programme_Subjects: s}) }} 
                              placeholder="e.g. Sports Biomechanics"
                              style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                            />
                          </div>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label style={{ display: "block", marginBottom: "8px", fontSize: "13px", fontWeight: "500", color: "#64748b" }}>Total Seats</label>
                            <input 
                              type="number"
                              value={sub.Total_Seats || ""} 
                              onChange={e => { const s = [...(formData.Programme_Subjects || [])]; s[idx].Total_Seats = e.target.value; setFormData({...formData, Programme_Subjects: s}) }} 
                              placeholder="e.g. 60"
                              style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                            />
                          </div>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label style={{ display: "block", marginBottom: "8px", fontSize: "13px", fontWeight: "500", color: "#64748b" }}>Group</label>
                            <input 
                              type="text"
                              value={sub.Group || ""} 
                              onChange={e => { const s = [...(formData.Programme_Subjects || [])]; s[idx].Group = e.target.value; setFormData({...formData, Programme_Subjects: s}) }} 
                              placeholder="e.g. Core / Elective"
                              style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button type="button" className="add-sub-btn" style={{ padding: "8px 16px", background: "#f1f5f9", color: "#334155", border: "1px dashed #cbd5e1", borderRadius: "6px", cursor: "pointer", fontWeight: "500", fontSize: "14px", width: "100%", marginBottom: "20px" }} onClick={() => setFormData({...formData, Programme_Subjects: [...(formData.Programme_Subjects || []), mkProgrammeSubject()]})}>
                    + Add New Subject
                  </button>

                  {/* Live Preview */}
                  {(formData.Programme_Name || formData.Sports_Specialization) && (
                    <div className="programme-preview">
                      <div className="preview-label">Live Preview Card</div>
                      <div className="preview-card" style={{ borderLeft: `4px solid ${getSpecColor(formData.Sports_Specialization)}` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <span className="preview-type-pill"
                            style={{ color: getTypeColor(formData.Programme_Type), background: TYPE_META[formData.Programme_Type]?.bg }}>
                            {formData.Programme_Type === "B.P.Ed" ? "🏃" : "🎓"} {formData.Programme_Type}
                          </span>
                          <span className="preview-code">{formData.Programme_Code || "CODE"}</span>
                        </div>
                        {formData.Sports_Specialization && (
                          <div className="preview-spec" style={{ color: getSpecColor(formData.Sports_Specialization) }}>● {formData.Sports_Specialization}</div>
                        )}
                        {formData.Accreditation_Approval && (
                          <div className="preview-accred">{formData.Accreditation_Approval}</div>
                        )}
                        <div className="preview-name">{formData.Programme_Name || "Programme Name"}</div>
                        <div className="preview-meta-row">
                          {formData.Duration   && <span>⏱ {formData.Duration}</span>}
                          {formData.Total_Seats && <span>👥 {formData.Total_Seats} Seats</span>}
                          {formData.Annual_Fees && <span>💰 {formData.Annual_Fees}/yr</span>}
                        </div>
                        {formData.Practical_Field_Training_Hours && (
                          <div className="preview-practical">⏰ {formData.Practical_Field_Training_Hours}</div>
                        )}
                        {formData.Fitness_Entrance_Test && (
                          <div className="preview-fitness">💪 {formData.Fitness_Entrance_Test}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Tab 3: Eligibility ── */}
              {activeTab === "eligibility" && (
                <div className="tab-content">
                  <div className="info-box">
                    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#0369a1" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    {formData.Programme_Type === "B.P.Ed"
                      ? "B.P.Ed requires 10+2 as minimum qualification. Mention sports participation level, minimum percentage, physical fitness test, and any sports-specific requirements."
                      : "M.P.Ed requires B.P.Ed or equivalent as minimum qualification. Mention the degree, percentage, sports background, and any physical fitness test requirements."}
                  </div>
                  <div className="form-group full">
                    <label>Minimum Qualification Required</label>
                    <input type="text" name="Minimum_Qualification_Required" value={formData.Minimum_Qualification_Required} onChange={handleChange}
                      placeholder="e.g. 10+2 (Any Stream) / B.P.Ed." />
                    <span className="field-hint">Short label displayed on the card and website.</span>
                  </div>
                  <div className="form-group full">
                    <label>Full Eligibility Criteria <span className="req">*</span></label>
                    <textarea name="Full_Eligibility_Criteria" value={formData.Full_Eligibility_Criteria} onChange={handleChange}
                      placeholder="e.g. 10+2 from a recognized board with minimum 50% marks. Candidates must have participated in sports/games at district/state level..."
                      rows="5" required />
                  </div>
                  {formData.Minimum_Qualification_Required && (
                    <div className="eligibility-preview">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#059669" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                      Requires: <strong>{formData.Minimum_Qualification_Required}</strong>
                      {formData.Full_Eligibility_Criteria && ` — ${formData.Full_Eligibility_Criteria.slice(0, 80)}${formData.Full_Eligibility_Criteria.length > 80 ? "..." : ""}`}
                    </div>
                  )}
                </div>
              )}

              {/* ── Tab 4: Settings ── */}
              {activeTab === "settings" && (
                <div className="tab-content">
                  <div className="form-group full">
                    <label>Publication Status</label>
                    <div className="status-selector">
                      <label className={`status-option ${formData.Status === "Published" ? "selected" : ""}`}>
                        <input type="radio" name="Status" value="Published" checked={formData.Status === "Published"} onChange={handleChange} />
                        <div className="status-option-content">
                          <div className="status-icon published">🌐</div>
                          <div>
                            <strong>Published</strong>
                            <span>Visible on the college public website</span>
                          </div>
                        </div>
                      </label>
                      <label className={`status-option ${formData.Status === "Draft" ? "selected" : ""}`}>
                        <input type="radio" name="Status" value="Draft" checked={formData.Status === "Draft"} onChange={handleChange} />
                        <div className="status-option-content">
                          <div className="status-icon draft">📝</div>
                          <div>
                            <strong>Draft</strong>
                            <span>Hidden from the public website</span>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                  <div className="settings-note">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/><circle cx="12" cy="12" r="3"/>
                    </svg>
                    Published programmes instantly appear on <strong>Academics → B.P.Ed / M.P.Ed Programme</strong> on the student-facing website.
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <div className="mf-left">
                  {activeTab !== "details" && (
                    <button type="button" className="btn btn-light" onClick={() => setActiveTab(TABS[TABS.indexOf(activeTab) - 1])}>
                      ← Previous
                    </button>
                  )}
                </div>
                <div className="mf-right">
                  <button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button>
                  {activeTab !== "settings" ? (
                    <button type="button" className="btn btn-primary" onClick={() => setActiveTab(TABS[TABS.indexOf(activeTab) + 1])}>
                      Next →
                    </button>
                  ) : (
                    <button type="submit" className="btn btn-primary">
                      {modalType === "add" ? "✓ Add Programme" : "✓ Update Programme"}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {modalType === "view" && selectedItem && (() => {
        const specColor = getSpecColor(selectedItem.Sports_Specialization);
        const typeColor = getTypeColor(selectedItem.Programme_Type);
        const hlList    = selectedItem.Programme_Highlights
          ? selectedItem.Programme_Highlights.split(",").map((h) => h.trim()).filter(Boolean)
          : [];
        return (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="bped-modal view-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-header-left">
                  <div className="modal-header-icon" style={{ background: typeColor + "20", color: typeColor }}>
                    {selectedItem.Programme_Type === "B.P.Ed" ? "🏃" : "🎓"}
                  </div>
                  <div>
                    <h2>{selectedItem.Programme_Name}</h2>
                    <span style={{ color: specColor }}>{selectedItem.Sports_Specialization || "Physical Education Programme"}</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span className={`bped-status-badge ${selectedItem.Status}`}>
                    {selectedItem.Status === "Published" ? "● Published" : "● Draft"}
                  </span>
                  <button type="button" className="modal-close" onClick={closeModal}>×</button>
                </div>
              </div>

              <div className="view-body">
                {/* Hero */}
                <div className="view-hero" style={{ borderLeft: `5px solid ${specColor}`, background: `linear-gradient(135deg, ${specColor}10, ${specColor}05)` }}>
                  <div className="view-hero-left">
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
                      <span className="view-type-tag" style={{ color: typeColor, background: typeColor + "18" }}>{selectedItem.Programme_Type}</span>
                      <span className="view-code-tag" style={{ color: specColor, background: specColor + "18" }}>{selectedItem.Programme_Code}</span>
                      {selectedItem.Accreditation_Approval && (
                        <span className="view-accred-tag">{selectedItem.Accreditation_Approval}</span>
                      )}
                    </div>
                    <p>{selectedItem.Programme_Description}</p>
                    {selectedItem.Minimum_Qualification_Required && (
                      <div className="view-qual-tag">
                        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="#22c55e" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                        Min. Qualification: <strong>{selectedItem.Minimum_Qualification_Required}</strong>
                      </div>
                    )}
                    {selectedItem.Practical_Field_Training_Hours && (
                      <div className="view-practicum-tag" style={{ borderColor: specColor, color: specColor }}>
                        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
                        {selectedItem.Practical_Field_Training_Hours}
                      </div>
                    )}
                    {selectedItem.Fitness_Entrance_Test && (
                      <div className="vm-practicum-box fitness">
                        <span className="vm-pb-icon">🩺</span>
                        {selectedItem.Fitness_Entrance_Test}
                      </div>
                    )}
                  </div>
                  <div className="view-hero-stats">
                    {[
                      { val: selectedItem.Duration,          label: "Duration"    },
                      { val: selectedItem.Total_Semesters,   label: "Semesters"   },
                      { val: selectedItem.Total_Seats,       label: "Seats"       },
                      { val: selectedItem.Annual_Fees || "—", label: "Annual Fees" },
                    ].map((s) => (
                      <div key={s.label} className="hero-stat">
                        <strong>{s.val}</strong>
                        <span>{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Subjects List */}
                {selectedItem.Programme_Subjects && selectedItem.Programme_Subjects.length > 0 && (
                  <div style={{ padding: "20px" }}>
                    <h3 style={{ fontSize: "15px", color: "#1e293b", marginBottom: "16px", borderBottom: "1px solid #e2e8f0", paddingBottom: "8px" }}>Programme Subjects</h3>
                    <div className="view-table-wrapper" style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "14px" }}>
                        <thead>
                          <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600" }}>#</th>
                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600" }}>Subject Name</th>
                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600" }}>Seats</th>
                            <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600" }}>Group</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedItem.Programme_Subjects.map((sub, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                              <td style={{ padding: "12px 16px", color: "#64748b" }}>{i + 1}</td>
                              <td style={{ padding: "12px 16px", fontWeight: "500", color: "#1e293b" }}>{sub.Subject_Name || "—"}</td>
                              <td style={{ padding: "12px 16px", color: "#475569" }}>{sub.Total_Seats || "—"}</td>
                              <td style={{ padding: "12px 16px", color: "#475569" }}>{sub.Group || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Info Grid */}
                <div className="view-section-label">Programme Information</div>
                <div className="view-info-grid">
                  {[
                    { label: "Type",           val: selectedItem.Programme_Type },
                    { label: "Mode",           val: selectedItem.Mode_of_Study  },
                    { label: "Specialization", val: selectedItem.Sports_Specialization || "—" },
                    { label: "Affiliation",    val: selectedItem.Affiliation    || "—" },
                    { label: "Accreditation",  val: selectedItem.Accreditation_Approval  || "—" },
                    { label: "Min. Qualification", val: selectedItem.Minimum_Qualification_Required || "—" },
                  ].map((i) => (
                    <div key={i.label} className="view-info-item">
                      <span>{i.label}</span>
                      <strong>{i.val}</strong>
                    </div>
                  ))}
                </div>

                {/* Eligibility */}
                {selectedItem.Full_Eligibility_Criteria && (
                  <>
                    <div className="view-section-label">Eligibility Criteria</div>
                    <div className="view-eligibility-box">
                      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#059669" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                      {selectedItem.Full_Eligibility_Criteria}
                    </div>
                  </>
                )}

                {/* Highlights */}
                {hlList.length > 0 && (
                  <>
                    <div className="view-section-label">Programme Highlights</div>
                    <div className="view-highlights">
                      {hlList.map((h, i) => (
                        <span key={i} className="highlight-tag" style={{ background: specColor + "14", color: specColor, border: `1px solid ${specColor}28` }}>
                          ✦ {h}
                        </span>
                      ))}
                    </div>
                  </>
                )}

                <div className="view-footer-actions">
                  <button type="button" className="btn btn-light" onClick={closeModal}>Close</button>
                  <button type="button" className="btn btn-primary" onClick={() => { closeModal(); openEditModal(selectedItem); }}>
                    Edit Programme
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default BPEdMPEdProgramme;